import { Fragment, useState } from "react";
import { Pencil, Eye } from "lucide-react";
import { useEntities, useEntity } from "@/lib/queries";
import { EntityDetail } from "@/components/EntityDetail";
import { RichView } from "@/components/rich/RichEditor";
import { CategoryBadge } from "@/components/CategoryBadge";
import { EntityPick, Empty } from "@/components/studio/widgets/Pickers";

const Preview = ({ id }) => {
  const { data: e, isLoading } = useEntity(id);
  if (isLoading) return <Empty>Loading note…</Empty>;
  if (!e) return <Empty testid="note-widget-missing">This note isn't available.</Empty>;
  const attrs = Object.entries(e.attributes || {}).filter(([, v]) => v).slice(0, 8);
  return (
    <div className="space-y-2" data-testid="note-widget-preview">
      <div className="flex items-center gap-2">
        <h4 className="font-display text-xl leading-tight text-[#F3EBDD]" data-testid="note-widget-name">{e.name}</h4>
        <CategoryBadge category={e.category} />
      </div>
      {e.summary && <p className="text-xs italic text-[#E8DFD1]">{e.summary}</p>}
      {attrs.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
          {attrs.map(([k, v]) => <Fragment key={k}><dt className="text-[#A8A090]">{k.replace(/_/g, " ")}</dt><dd className="text-[#F3EBDD] [overflow-wrap:anywhere]">{String(v)}</dd></Fragment>)}
        </dl>
      )}
      {e.content && <RichView markdown={e.content} className="text-[13px] leading-relaxed" testid="note-widget-content" />}
    </div>
  );
};

export const NoteWidget = ({ uid, cfg, set }) => {
  const { data: entities = [] } = useEntities(uid);
  const [edit, setEdit] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <div className="min-w-0 flex-1"><EntityPick entities={entities} value={cfg.id} onChange={(id) => { set({ id }); setEdit(false); }} testid="note-widget-pick" /></div>
        {cfg.id && (
          <button onClick={() => setEdit(!edit)} title={edit ? "Back to preview" : "Edit here"} className="flex h-8 items-center gap-1 rounded border border-[#2D3442] px-2 text-[11px] text-[#F59E0B] hover:bg-[#1A202C]" data-testid="note-widget-edit-toggle">
            {edit ? <><Eye className="h-3.5 w-3.5" />Preview</> : <><Pencil className="h-3.5 w-3.5" />Edit here</>}
          </button>
        )}
      </div>
      {!cfg.id ? <Empty testid="note-widget-empty">Pick a note to keep it beside your chapter.</Empty>
        : edit ? <div className="text-sm" data-testid="note-widget-editor"><EntityDetail entityId={cfg.id} compact onNavigate={(id) => set({ id })} onDeleted={() => set({ id: null })} /></div>
          : <Preview id={cfg.id} />}
    </div>
  );
};
